import { Controller } from "@hotwired/stimulus"
import Editor from '@toast-ui/editor';

// Connects to data-controller="toast-ui"
export default class extends Controller {
  static targets = [ 'toast', 'form', 'mentionPopup' ];

  connect() {
    // 前回保存したモード（なければmarkdown）
    const savedMode = localStorage.getItem('toastEditorMode')
    const initialMode = (savedMode === 'wysiwyg') ? 'wysiwyg' : 'markdown'

    // 下書きの復元
    this.draftKey = this.element.dataset.draftKey
    let initialValue = this.formTarget.value
    if (this.draftKey) {
      const draft = localStorage.getItem(this.draftKey)
      if (draft !== null) {
        initialValue = draft
        this.formTarget.value = draft
      }
    }

    this.editor = new Editor({
      el: this.toastTarget,
      toolbarItems: [
        ['heading','bold','italic'],
        ['ul','ol'],
        ['codeblock','table','link','image']
      ],
      initialEditType: initialMode, // ← ここで初期モードを指定
      initialValue: initialValue,
      placeholder: this.element.dataset.placeholder,
      autofocus: this.element.dataset.focus === '1'
    })

    this.#adjustEditorHeight();

    this.editor.on('change', () => {
      this.#syncForm()
      this.#saveDraft()
      this.#adjustEditorHeight()
    });

    // DOMから現在のモードを取得し、ローカルストレージに保存
    this.editor.on('changeMode', () => {
      const mode = this.editor.isWysiwygMode() ? 'wysiwyg' : 'markdown';
      localStorage.setItem('toastEditorMode', mode);
    });

    // タブクリック時の高さ調整
    const tabs = this.element.querySelector('.toastui-editor-tabs').querySelectorAll('.tab-item')
    tabs[0].addEventListener('click', () => {
      // Writeクリック
      setTimeout(() => {
        this.#adjustEditorHeight()
      }, 500)
    })
    tabs[1].addEventListener('click', () => {
      // Previewクリック
      setTimeout(() => {
        this.#adjustPreviewHeight()
      }, 500)
    })

    this.editor.addHook('addImageBlobHook', async (blob, callback) => {
      const imageUrl = await this.#uploadFile(blob);
      callback(imageUrl, blob.name);
    });

    this.#setupListener();

    // フォーム送信成功時に下書きをクリア
    if (this.draftKey) {
      this.#setupDraftClear()
    }
  }

  disconnect() {
    if (this._draftTimer) clearTimeout(this._draftTimer)
  }

  #syncForm() {
    this.formTarget.value = this.editor.getMarkdown();
  }

  #saveDraft() {
    if (!this.draftKey) return
    if (this._draftTimer) clearTimeout(this._draftTimer)
    this._draftTimer = setTimeout(() => {
      const content = this.editor.getMarkdown()
      if (content.trim() === '') {
        localStorage.removeItem(this.draftKey)
      } else {
        localStorage.setItem(this.draftKey, content)
      }
    }, 300)
  }

  #setupDraftClear() {
    const form = this.element.closest('form')
    if (!form) return

    form.addEventListener('turbo:submit-end', (event) => {
      if (event.detail.success) {
        localStorage.removeItem(this.draftKey)
      }
    })
  }

  #getCsrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta && meta.getAttribute('content');
  }

  // エディタ本文の最大高さ。超えた分はエディタ内でスクロールする
  #maxHeight() {
    return parseInt(this.element.dataset.maxHeight, 10) || 800;
  }

  #adjustEditorHeight() {
    let contentElement = this.element.querySelector('.ProseMirror');
    if (contentElement.offsetHeight === 0) {
      // wysiwygの場合は、toastui-editor-contentsも含めて要素特定
      contentElement = this.element.querySelector('.ProseMirror.toastui-editor-contents');
    }
    let computedStyle = window.getComputedStyle(contentElement)
    let contentHeight = parseFloat(computedStyle.paddingTop) + parseFloat(computedStyle.paddingBottom)
    for (const el of contentElement.children) {
      contentHeight += el.offsetHeight
    }

    if (contentHeight <= 125) {
      this.editor.setHeight(`200px`);
      return;
    }
    contentHeight += 1
    let newHeight = Math.min(contentHeight, this.#maxHeight());

    this.editor.setHeight(`${newHeight + 75}px`);
  }

  #adjustPreviewHeight() {
    let contentElement = this.element.querySelector('.toastui-editor-contents');
    let contentHeight = contentElement.offsetHeight
    if (contentHeight <= 125) {
      this.editor.setHeight(`200px`);
      return;
    }
    contentHeight += 1
    const newHeight = Math.min(contentHeight, this.#maxHeight());

    this.editor.setHeight(`${newHeight + 95}px`);
  }

  async #uploadFile(file) {
    const formData = new FormData();
    formData.append('file', file);

    // hiddenフィールドからspace_idを取得
    const spaceIdElement = document.getElementById('current_space_id');
    const spaceId = spaceIdElement ? spaceIdElement.value : null;

    if (!spaceId) {
      alert('スペースが選択されていません');
      return;
    }

    try {
      const response = await fetch(`/${spaceId}/document/attachments`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.#getCsrfToken(), // CSRFトークンをヘッダーに追加
        },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        return data.url;
      } else {
        const data = await response.json();
        const errorMessage = data.error || '画像のアップロードに失敗しました';
        alert(errorMessage);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('通信エラーが発生しました');
    }
  }

  #setupListener() {
    this.mentionPopupEl = this.mentionPopupTarget.content.cloneNode(true).firstElementChild;
    const self = this;
    let isShiftPressed = false;
    let shiftReleaseTimer = null;
    let isPopupVisible = false;
    let currentSelection = -1;

    // メンションの選択処理を共通化
    const selectMention = (element) => {
      const mentionName = element.dataset.mentionName.replace(/[ 　]/g, '');
      const mentionId = element.dataset.mentionId;
      // マークダウンリンク形式: [@山本](mention:123)
      const linkText = `[${mentionName}](mention:${mentionId})`;

      if (self.editor.isWysiwygMode()) {
        // WYSIWYGモード: マークダウンを直接操作
        const markdown = self.editor.getMarkdown();
        // 最後の "@" を置換
        const newMarkdown = markdown.replace(/@([^@]*)$/, `${linkText} `);
        self.editor.setMarkdown(newMarkdown);
      } else {
        // Markdownモード
        const [start, end] = self.editor.getSelection();
        self.editor.replaceSelection(`${linkText} `, [start[0], start[1] - 1], end);
      }
      hidePopup();
    };

    const selectItem = (index) => {
      const items = this.mentionPopupEl.querySelectorAll('.mention-item');
      items.forEach(item => item.classList.remove('active'));

      if (index >= 0 && index < items.length) {
        currentSelection = index;
        items[index].classList.add('active');
        items[index].scrollIntoView({ block: 'nearest' });
      }
    };

    const confirmSelection = () => {
      const items = this.mentionPopupEl.querySelectorAll('.mention-item');
      if (currentSelection >= 0 && currentSelection < items.length) {
        const selectedItem = items[currentSelection];
        const element = selectedItem.querySelector('.value') || selectedItem;
        selectMention(element);
      }
    };

    const hidePopup = () => {
      if (isPopupVisible) {
        this.mentionPopupEl.remove();
        isPopupVisible = false;
        currentSelection = -1;
      }
    };

    const handleKeyDown = (editorType, event) => {
      if (isPopupVisible) {
        const items = this.mentionPopupEl.querySelectorAll('.mention-item');

        switch (event.key) {
          case 'ArrowUp':
            event.preventDefault();
            selectItem(currentSelection <= 0 ? items.length - 1 : currentSelection - 1);
            break;
          case 'ArrowDown':
            event.preventDefault();
            selectItem(currentSelection >= items.length - 1 ? 0 : currentSelection + 1);
            break;
          case 'Enter':
            event.preventDefault();
            confirmSelection();
            break;
          case 'Escape':
            event.preventDefault();
            hidePopup();
            break;
        }
      }

      if (event.key === 'Shift') {
        isShiftPressed = true;
        if (shiftReleaseTimer) {
          clearTimeout(shiftReleaseTimer);
          shiftReleaseTimer = null;
        }
      }
    };

    const handleKeyUp = (editorType, event) => {
      if (event.key === 'Shift') {
        shiftReleaseTimer = setTimeout(() => {
          isShiftPressed = false;
          shiftReleaseTimer = null;
        }, 300);
        return;
      }

      if ((isShiftPressed && event.key === '2') || event.key === '@') {
        hidePopup();
        self.editor.addWidget(self.mentionPopupEl, 'top');
        isPopupVisible = true;
        selectItem(0);
      }
    };

    this.editor.on('keydown', handleKeyDown);
    this.editor.on('keyup', handleKeyUp);

    // マウスクリックでの選択処理
    this.mentionPopupEl.addEventListener("mousedown", function(event) {
      let element = event.target.querySelector('.value');
      element ||= event.target;
      selectMention(element);
    });
  }
}